// `users`: Swift `User` (GroundTeam/Models/User.swift). Decoding template `User(firstName: "", lastName: "")`
// (GroundTeam/Sync/ResilientDecoding.swift:75). `lastLocation` / `lastLocationAt` are local-only (:77): every device
// derives live positions from the synced day logs, so they are never on the wire and are ignored when present.

import { USER_ROLES, type UserRole } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  DATE,
  STRING,
  UUID,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface UserPayload extends Passthrough {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: UserRole;
  teamId?: string;
  managerId?: string;
  colorHex: string;
  isActive: boolean;
  title: string;
  createdAt: string;
  trackingEnabled: boolean;
  /** CloudKit-era iCloud user record name. Legacy, but a real key: kept, never cleared by the web. */
  cloudUserRecordName?: string;
}

/** `User.localOnlyKeys`. */
export const USER_LOCAL_ONLY_KEYS = ['lastLocation', 'lastLocationAt'] as const;

/** Stored properties of User in declaration order, minus the local-only ones. */
export const USER_KEYS = [
  'id',
  'firstName',
  'lastName',
  'email',
  'phone',
  'role',
  'teamId',
  'managerId',
  'colorHex',
  'isActive',
  'title',
  'createdAt',
  'trackingEnabled',
  'cloudUserRecordName',
] as const satisfies readonly (keyof Known<UserPayload>)[];

export const DEFAULT_USER_COLOR = '#2673E6';

export const USER_FIELDS: FieldMap<UserPayload> = {
  id: field.strict(UUID),
  firstName: field.req(STRING, () => ''),
  lastName: field.req(STRING, () => ''),
  email: field.req(STRING, () => ''),
  phone: field.req(STRING, () => ''),
  role: field.req(enumOf('UserRole', USER_ROLES), () => 'rep'),
  teamId: field.opt(UUID),
  managerId: field.opt(UUID),
  colorHex: field.req(STRING, () => DEFAULT_USER_COLOR),
  isActive: field.req(BOOL, () => true),
  title: field.req(STRING, () => ''),
  createdAt: field.req(DATE, nowWire),
  trackingEnabled: field.req(BOOL, () => true),
  cloudUserRecordName: field.opt(STRING),
};

export function decodeUser(raw: JsonObject, envelopeId?: string): Decoded<UserPayload> {
  return decodeModel(USER_FIELDS, raw, { localOnly: USER_LOCAL_ONLY_KEYS }, envelopeId);
}

export type UserInit = Partial<Known<UserPayload>> & { firstName: string; lastName: string };

export function newUser(init: UserInit): UserPayload {
  const defaults = {
    id: newId(),
    email: '',
    phone: '',
    role: 'rep',
    colorHex: DEFAULT_USER_COLOR,
    isActive: true,
    title: '',
    createdAt: nowWire(),
    trackingEnabled: true,
  };
  return createModel(USER_FIELDS, defaults, init, 'users', { localOnly: USER_LOCAL_ONLY_KEYS });
}
